import { useEffect, useState } from "react";
import api from "@/api/client";
import BannerCarousel from "@/components/BannerCarousel";
import AnnouncementStrip from "@/components/AnnouncementStrip";
import LiveHero from "@/components/LiveHero";
import LiveVendorBanners from "@/components/LiveVendorBanners";
import DropCountdown from "@/components/DropCountdown";
import ProductCard from "@/components/ProductCard";
import CuratedDropSection from "@/components/CuratedDropSection";
import { useSiteContent } from "@/context/SiteContentContext";

export default function Home() {
  const { t } = useSiteContent();
  const [banners, setBanners] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imgFailed, setImgFailed] = useState(false);
  const uspImageSrc = t("usp_image", "/images/usp-curated-exhibition.svg");

  useEffect(() => {
    Promise.all([
      api.get("/banners"),
      api.get("/products?limit=20"),
    ]).then(([b, p]) => {
      setBanners(b.data); setProducts(p.data);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setImgFailed(false);
  }, [uspImageSrc]);

  return (
    <div data-testid="home-page" className="pb-24">
      <BannerCarousel banners={banners} />
      <AnnouncementStrip />
      <LiveHero />
      <LiveVendorBanners />
      <DropCountdown />

      <section className="mx-auto max-w-7xl px-4 lg:px-8 mt-3 sm:mt-6 md:mt-10">
        <div className="flex items-end justify-between mb-3 sm:mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#F48E2E] font-bold mb-2">{t("home.live_kicker", "Live now")}</div>
            <h2 className="font-display text-3xl md:text-4xl">{t("home.live_headline", "Today's Curated Products")}</h2>
          </div>
        </div>
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-[4/5] bg-gray-100 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-10" data-testid="home-empty-state">
            <div className="text-xs uppercase tracking-[0.3em] text-[#F48E2E] font-bold mb-2">The next drop is warming up</div>
            <h3 className="font-display text-2xl mb-3">Today's collection is being curated</h3>
            <p className="text-gray-600 max-w-xl mx-auto">Fresh handpicked products go live every day for 12 to 24 hours. Browse categories below while our exhibitors prepare their next drop.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="product-grid">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      <CuratedDropSection />

      <section className="mx-auto max-w-7xl px-4 lg:px-8 mt-4 sm:mt-6" data-testid="trust-strip">
        <div className="rounded-2xl bg-[#111827] text-white px-3 sm:px-4 md:px-8 py-2.5 sm:py-4 grid grid-cols-4 gap-2 sm:gap-4 md:gap-6">
          {[1,2,3,4].map((n) => (
            <div key={n} className="flex flex-col items-center sm:items-start text-center sm:text-left">
              <div className="text-[9px] sm:text-sm md:text-base font-semibold text-white leading-tight">{t(`home.trust_${n}_title`, "")}</div>
              <div className="hidden sm:block text-[11px] md:text-xs text-white/60 mt-0.5">{t(`home.trust_${n}_body`, "")}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 lg:px-8 mt-8 md:mt-12" data-testid="usp-section">
        <div className="rounded-3xl border border-gray-200 bg-white overflow-hidden">
          <div className="grid md:grid-cols-[1.1fr_1fr]">
            <div className="p-5 md:p-8">
              <div className="text-xs uppercase tracking-[0.3em] text-[#F48E2E] font-bold mb-3">{t("home.usp_kicker", "Why CityShor")}</div>
              <h2 className="font-display text-3xl md:text-4xl leading-tight mb-4">{t("home.usp_headline", "A different way to shop online.")}</h2>
              <p className="text-gray-600 mb-6">
                {t("home.usp_paragraph", "CityShor Pop-Up Bazaar is not endless scrolling. Every day, a small set of products from independent Indian brands and exhibition sellers goes live for a limited window and then disappears.")}
              </p>
              <ul className="space-y-3">
                {[
                  t("home.usp_bullet_1", "Handpicked products. No endless scrolling."),
                  t("home.usp_bullet_2", "A new set of products goes live every day for 12 to 24 hours."),
                  t("home.usp_bullet_3", "Bought directly from independent exhibition sellers and small brands."),
                  t("home.usp_bullet_4", "Once a drop ends, the products go off the site."),
                  t("home.usp_bullet_5", "Fashion, gifts, kids, accessories and more."),
                  t("home.usp_bullet_6", "Small businesses reach buyers nationwide without building their own website."),
                  t("home.usp_bullet_7", "Products you would not usually find on regular e-commerce sites."),
                ].map((bullet, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="text-gray-800">{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative min-h-[220px] md:h-[320px] md:self-center" data-testid="usp-image-wrap">
              {imgFailed ? (
                <div
                  data-testid="usp-image-fallback"
                  className="absolute inset-0 flex items-center justify-center text-center px-6"
                  style={{ background: "linear-gradient(135deg, #F48E2E 0%, #F6B266 45%, #FDEBD3 100%)" }}
                >
                  <div className="max-w-sm">
                    <div className="text-white/90 uppercase tracking-[0.3em] text-[10px] md:text-xs mb-2">{t("home.usp_image_kicker", "Today's promise")}</div>
                    <div className="font-display text-2xl md:text-3xl text-white drop-shadow-sm">{t("home.usp_image_fallback_headline", "Curated goods from India's finest exhibitors.")}</div>
                  </div>
                </div>
              ) : (
                <>
                  <img
                    src={uspImageSrc}
                    alt="Curated exhibition"
                    onError={() => setImgFailed(true)}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-transparent to-transparent" />
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
